import { addDays, differenceInCalendarDays, endOfDay, isSameDay, startOfDay } from 'date-fns';
import { CalendarEvent } from '../types/calendar';
import { effectiveEndMs } from './calendarEvents';

/**
 * How an event is drawn: a `timed` chip (dot, time, title) for something that
 * starts and ends at set times within one day, or a `block` bar for anything
 * that owns whole days (no end time, a full-day window, or several days).
 */
export type CalendarEventShape = 'timed' | 'block';

/** An event resolved to the local calendar days it covers. */
export interface EventSpan<E extends CalendarEvent = CalendarEvent> {
  event: E;
  start: Date;
  /** Effective end instant; see `effectiveEndMs`. */
  end: Date;
  /** The end as entered, for labels ("22:00 – 00:00"); `end` is for day maths. */
  displayEnd: Date;
  /** Local start of the first day covered. */
  firstDay: Date;
  /** Local start of the last day covered. */
  lastDay: Date;
  /** Calendar days covered, at least 1. */
  dayCount: number;
  shape: CalendarEventShape;
}

/** The part of a span that falls inside one week row. */
export interface WeekSegment<E extends CalendarEvent = CalendarEvent> {
  span: EventSpan<E>;
  /** Column of the first day in this row (0-based). */
  startCol: number;
  /** Column of the last day in this row (0-based, inclusive). */
  endCol: number;
  /** Row inside the week. Segments that share a lane never overlap. */
  lane: number;
  /** The event started in an earlier row, so the bar has no left end. */
  continuesBefore: boolean;
  /** The event carries on into a later row, so the bar has no right end. */
  continuesAfter: boolean;
}

export interface WeekLayout<E extends CalendarEvent = CalendarEvent> {
  weekStart: Date;
  /** The days of the row, in column order. */
  days: Date[];
  /** Sorted by lane, then by column. */
  segments: WeekSegment<E>[];
  /** Lanes used by the row; 0 when it has no events. */
  laneCount: number;
}

const DAYS_PER_WEEK = 7;

function classify(event: CalendarEvent, start: Date, end: Date): CalendarEventShape {
  // No end time means an all-day event (see docs/argus/calendar.md).
  if (event.end_time == null) return 'block';
  if (!isSameDay(start, end)) return 'block';
  // Midnight to (almost) midnight is a whole day, not a time slot.
  const wholeDay =
    start.getTime() === startOfDay(start).getTime() &&
    end.getTime() >= endOfDay(start).getTime() - 60_000;
  return wholeDay ? 'block' : 'timed';
}

/** Resolve an event to the days it covers, or null when its start is unparseable. */
export function getEventSpan<E extends CalendarEvent>(event: E): EventSpan<E> | null {
  const start = new Date(event.start_time);
  if (isNaN(start.getTime())) return null;
  const end = new Date(effectiveEndMs(start.getTime(), event.end_time));
  const rawEnd = event.end_time != null ? new Date(event.end_time) : null;
  const displayEnd = rawEnd && !isNaN(rawEnd.getTime()) && rawEnd.getTime() >= start.getTime() ? rawEnd : end;
  const firstDay = startOfDay(start);
  const lastDay = startOfDay(end);
  return {
    event,
    start,
    end,
    displayEnd,
    firstDay,
    lastDay,
    dayCount: differenceInCalendarDays(lastDay, firstDay) + 1,
    shape: classify(event, start, end),
  };
}

/** Spans for every event with a usable start time, in input order. */
export function getEventSpans<E extends CalendarEvent>(events: readonly E[]): EventSpan<E>[] {
  const spans: EventSpan<E>[] = [];
  for (const event of events) {
    const span = getEventSpan(event);
    if (span) spans.push(span);
  }
  return spans;
}

/** Events that cover at least one day in `[rangeStart, rangeEnd]`. */
export function eventsOverlapping<E extends CalendarEvent>(
  events: readonly E[],
  rangeStart: Date,
  rangeEnd: Date,
): E[] {
  const first = startOfDay(rangeStart).getTime();
  const last = startOfDay(rangeEnd).getTime();
  if (last < first) return [];
  return getEventSpans(events)
    .filter(span => span.firstDay.getTime() <= last && span.lastDay.getTime() >= first)
    .map(span => span.event);
}

/**
 * Packing order: earlier columns first; on the same column, block bars before
 * timed chips, longer bars before shorter ones, then by start time and title.
 * Sorting before the greedy lane pass keeps long bars on top and continuous.
 */
function compareForPacking<E extends CalendarEvent>(a: WeekSegment<E>, b: WeekSegment<E>): number {
  if (a.startCol !== b.startCol) return a.startCol - b.startCol;
  if (a.span.shape !== b.span.shape) return a.span.shape === 'block' ? -1 : 1;
  const aLength = a.endCol - a.startCol;
  const bLength = b.endCol - b.startCol;
  if (aLength !== bLength) return bLength - aLength;
  const byStart = a.span.start.getTime() - b.span.start.getTime();
  if (byStart !== 0) return byStart;
  return a.span.event.title.localeCompare(b.span.event.title);
}

/**
 * Lay out one week row: clip every span to the row, then pack the pieces into
 * lanes greedily so that a bar takes the first lane free across all of its
 * columns. `weekStart` must be the local start of the row's first day.
 */
export function layoutWeek<E extends CalendarEvent>(
  spans: readonly EventSpan<E>[],
  weekStart: Date,
): WeekLayout<E> {
  const start = startOfDay(weekStart);
  const days: Date[] = [];
  for (let i = 0; i < DAYS_PER_WEEK; i++) days.push(addDays(start, i));
  const weekEnd = days[DAYS_PER_WEEK - 1];

  const segments: WeekSegment<E>[] = [];
  for (const span of spans) {
    if (span.lastDay.getTime() < start.getTime() || span.firstDay.getTime() > weekEnd.getTime()) continue;
    const continuesBefore = span.firstDay.getTime() < start.getTime();
    const continuesAfter = span.lastDay.getTime() > weekEnd.getTime();
    segments.push({
      span,
      startCol: continuesBefore ? 0 : differenceInCalendarDays(span.firstDay, start),
      endCol: continuesAfter ? DAYS_PER_WEEK - 1 : differenceInCalendarDays(span.lastDay, start),
      lane: 0,
      continuesBefore,
      continuesAfter,
    });
  }

  segments.sort(compareForPacking);

  // occupied[lane][col]
  const occupied: boolean[][] = [];
  for (const segment of segments) {
    let lane = 0;
    for (;;) {
      const row = occupied[lane] ?? (occupied[lane] = new Array<boolean>(DAYS_PER_WEEK).fill(false));
      let free = true;
      for (let col = segment.startCol; col <= segment.endCol; col++) {
        if (row[col]) {
          free = false;
          break;
        }
      }
      if (free) {
        for (let col = segment.startCol; col <= segment.endCol; col++) row[col] = true;
        segment.lane = lane;
        break;
      }
      lane++;
    }
  }

  segments.sort((a, b) => (a.lane !== b.lane ? a.lane - b.lane : a.startCol - b.startCol));

  return { weekStart: start, days, segments, laneCount: occupied.length };
}

/**
 * Lay out every week row of a grid. `gridStart` must be the first day of a
 * row; rows are produced until `gridEnd` (inclusive) is covered.
 */
export function layoutWeeks<E extends CalendarEvent>(
  events: readonly E[],
  gridStart: Date,
  gridEnd: Date,
): WeekLayout<E>[] {
  const spans = getEventSpans(events);
  const weeks: WeekLayout<E>[] = [];
  const last = startOfDay(gridEnd).getTime();
  for (let weekStart = startOfDay(gridStart); weekStart.getTime() <= last; weekStart = addDays(weekStart, DAYS_PER_WEEK)) {
    weeks.push(layoutWeek(spans, weekStart));
  }
  return weeks;
}

/**
 * How many events in each column sit in lanes at or beyond `visibleLanes`,
 * i.e. would be hidden when the row shows at most that many lanes.
 */
export function hiddenCountsByColumn(layout: WeekLayout, visibleLanes: number): number[] {
  const counts = new Array<number>(layout.days.length).fill(0);
  for (const segment of layout.segments) {
    if (segment.lane < visibleLanes) continue;
    for (let col = segment.startCol; col <= segment.endCol; col++) counts[col]++;
  }
  return counts;
}

/** 1-based position of `day` inside a span, for "Day 2 of 5" labels. */
export function dayIndexInSpan(span: EventSpan, day: Date): number {
  return differenceInCalendarDays(startOfDay(day), span.firstDay) + 1;
}
